import { toast } from '@avila-roffe/components-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAgentForApp,
  deleteAgentByName,
  listAgentsForApp,
  listModelRegistry,
  syncModelRegistry,
} from '../agentApi';
import type { Agent, CreateAgentDto } from '../types';

export const agentKeys = {
  all: ['agents'] as const,
  byApp: (appSlug: string) => [...agentKeys.all, 'app', appSlug] as const,
};

export function useAgents(appSlug: string) {
  return useQuery({
    queryKey: agentKeys.byApp(appSlug),
    queryFn: () => listAgentsForApp(appSlug || undefined),
    staleTime: 60_000,
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentDto) => createAgentForApp(data),
    onSuccess: (agent: Agent | null) => {
      void qc.invalidateQueries({ queryKey: agentKeys.all });
      if (agent) toast.success(`Agent "${agent.name}" created`);
    },
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => deleteAgentByName(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.all });
      toast.success('Agent deleted');
    },
  });
}

export const modelRegistryKeys = {
  all: ['model-registry'] as const,
};

export function useModelRegistry() {
  return useQuery({
    queryKey: modelRegistryKeys.all,
    queryFn: listModelRegistry,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSyncModelRegistry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: syncModelRegistry,
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: modelRegistryKeys.all });
      toast.success(`Synced ${result.synced} models`);
    },
  });
}
